import React, { useState } from 'react';
import { useLoadFavorites } from 'App/components/FavoritePages/data';
import Logo from 'App/components/Logo';
import NavigationSidePage from 'App/components/NavigationSidePage';
import { SubscriptionsSidePage } from 'App/components/Subscriptions';
import { useLoadSubscriptions } from 'App/components/Subscriptions/data';
import { UserMenu, UserOpener } from 'App/components/UserMenu';
import { isGoGameEnv } from 'App/constants';
import { useIsImpersonated, useUserPages } from 'Auth';
import clsx from 'clsx';

import {
  AppBar,
  makeStyles,
  Stack,
  IconButton,
  Link,
  Fab,
  MenuIcon,
  CloseIcon,
  AIIcon,
  FeedbackOutlinedIcon,
  ContactSupportOutlinedIcon,
  NewspaperIcon,
  MailClockOutlineIcon,
  LIGHT_THEME_ID,
  LightModeOutlinedIcon,
  DARK_THEME_ID,
  DarkModeOutlinedIcon,
  useTheme,
  useThemeSwitcher,
  Tooltip,
} from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import { ScreenLink, KnowledgeBaseLink } from '@gogame/screens';

import { getDefaultPartnersRoute } from 'scenes/partnerRoutes';
import { getDefaultRoute } from 'scenes/routes';
import { GlobalAIChatDialog } from 'App/components/GlobalAIChatDialog';

import { News } from '../News';
import { Notifications } from '../Notifications';
import { NotificationsOpener } from '../Notifications/NotificationsOpener';
import { useEmailCallbackSidePage } from './useEmailCallbackSidePage';
import { ZENDESK_URL } from './constants';
import { permission as AIChatbotPermissionId } from 'scenes/AIChatbot';

const useStyles = makeStyles(
  (theme) => {
    return {
      container: {
        width: '64px',
        height: '100vh',
      },
      impersonated: {
        background: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
      },
      normal: {
        background: theme.palette.background.paper,
        color: theme.palette.text.secondary,
      },
      appBar: {
        width: '64px',
        height: '100%',
        left: '0',
        right: 'auto',
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingRight: '0!important',
        // zIndex: theme.zIndex.drawer + 3,
      },
      active: {
        background: `${theme.palette.action.hover}`,
      },
      logo: {
        height: '64px',
        width: '64px',
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      bottom: {
        paddingBottom: '20px',
      },
    };
  },
  { name: 'GgSidebarToolbar' }
);

export const SidebarToolbar = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { setActiveThemeId } = useThemeSwitcher();
  const [childComponent, openChildComponent] = useChildComponent();
  const [chatBotChildComponent, openChatBotChildComponent] =
    useChildComponent();
  const [activeDrawerName, setActiveDrawerName] = useState();
  const toggleDrawer = async (component, props) => {
    if (childComponent && component?.name === childComponent?.type?.name) {
      await childComponent.props.onClose();
    } else {
      setActiveDrawerName(component?.name);
      await openChildComponent(component, {
        ...props,
        onClose: () => {
          setActiveDrawerName(null);
        },
      });
    }
  };
  useEmailCallbackSidePage(toggleDrawer);
  const impersonated = useIsImpersonated();

  useLoadFavorites();

  const pages = useUserPages();

  const defaultRoute = isGoGameEnv
    ? getDefaultRoute()
    : getDefaultPartnersRoute(pages);

  useLoadSubscriptions();
  const handleOpenNews = async () => {
    await toggleDrawer(News);
  };
  const handleOpenNavigation = async () => {
    await toggleDrawer(NavigationSidePage);
  };
  const handleOpenUserMenu = async () => {
    await toggleDrawer(UserMenu);
  };
  const handleOpenSubscriptions = async () => {
    await toggleDrawer(SubscriptionsSidePage);
  };

  const handleSetDarkTheme = () => {
    setActiveThemeId(DARK_THEME_ID);
  };

  const handleSetLightTheme = () => {
    setActiveThemeId(LIGHT_THEME_ID);
  };

  const handleToggleChatBot = () => {
    if (chatBotChildComponent) {
      openChatBotChildComponent(null);
    } else {
      openChatBotChildComponent(GlobalAIChatDialog);
    }
  };

  const isAllowChatBot = pages.includes(AIChatbotPermissionId);
  return (
    <>
      <AppBar
        position={'fixed'}
        className={clsx(classes.appBar, {
          [classes.impersonated]: impersonated,
          [classes.normal]: !impersonated,
        })}
        elevation={0}
        data-cy={'screenSidebar'}
      >
        <Stack spacing={1} alignItems={'center'} grow={1}>
          <div className={clsx(classes.logo)}>
            <ScreenLink link={defaultRoute}>
              <Logo impersonated={impersonated} />
            </ScreenLink>
          </div>

          <IconButton
            icon={<MenuIcon />}
            className={clsx({
              [classes.active]: activeDrawerName === NavigationSidePage?.name,
            })}
            onClick={handleOpenNavigation}
            color={'inherit'}
            data-cy={'navigation'}
            tooltip={'Navigation'}
            tooltipPlacement={'right'}
          />
          <UserOpener
            onClick={handleOpenUserMenu}
            className={clsx({
              [classes.active]: activeDrawerName === UserMenu?.name,
            })}
          />
          <NotificationsOpener
            openChildComponent={toggleDrawer}
            className={clsx({
              [classes.active]: activeDrawerName === Notifications?.name,
            })}
          />
          <IconButton
            icon={<MailClockOutlineIcon />}
            tooltip={'Subscriptions'}
            tooltipPlacement={'right'}
            onClick={handleOpenSubscriptions}
            color={'inherit'}
            data-cy={'subscriptions'}
            className={clsx({
              [classes.active]:
                activeDrawerName === SubscriptionsSidePage?.name,
            })}
          />
          <IconButton
            icon={<NewspaperIcon />}
            tooltip={'News'}
            tooltipPlacement={'right'}
            onClick={handleOpenNews}
            color={'inherit'}
            data-cy={'news'}
            className={clsx({
              [classes.active]: activeDrawerName === News?.name,
            })}
          />
        </Stack>

        <Stack spacing={1} alignItems={'center'} className={classes.bottom}>
          {theme.palette.mode === LIGHT_THEME_ID ? (
            <IconButton
              tooltip={'Set dark theme'}
              tooltipPlacement={'right'}
              color={'inherit'}
              icon={<DarkModeOutlinedIcon />}
              onClick={handleSetDarkTheme}
            />
          ) : null}
          {theme.palette.mode === DARK_THEME_ID ? (
            <IconButton
              tooltip={'Set light theme'}
              tooltipPlacement={'right'}
              color={'inherit'}
              icon={<LightModeOutlinedIcon />}
              onClick={handleSetLightTheme}
            />
          ) : null}

          <Link
            href={ZENDESK_URL}
            target={'_blank'}
            color={'inherit'}
            underline={'none'}
          >
            <IconButton
              icon={<FeedbackOutlinedIcon />}
              tooltip={'Contact us'}
              tooltipPlacement={'right'}
              color={'inherit'}
            />
          </Link>

          <KnowledgeBaseLink>
            <IconButton
              icon={<ContactSupportOutlinedIcon />}
              tooltip={'Knowledge base'}
              tooltipPlacement={'right'}
              color={'inherit'}
            />
          </KnowledgeBaseLink>
          {isAllowChatBot && (
            <Tooltip
              title={
                chatBotChildComponent
                  ? 'Close Chat with AI Agent'
                  : 'Chat with AI Agent'
              }
              placement={'right'}
              wrapper={true}
            >
              <Fab
                color={'primary'}
                size={'small'}
                onClick={handleToggleChatBot}
              >
                {chatBotChildComponent ? <CloseIcon /> : <AIIcon />}
              </Fab>
            </Tooltip>
          )}
        </Stack>
      </AppBar>
      {childComponent}
      {chatBotChildComponent}
    </>
  );
};
